import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar.jsx";
import { t } from "../i18n";
import MediaSelector from "../components/MediaSelector.jsx";
import { fetchBillingStatus, fetchCreditEstimate } from "../services/billing.js";
import "./TripoStudio.css";

const isRemoteUrl = (url) => /^https?:\/\//i.test(url);

import { buildDownloadName, downloadFile } from "../utils/download";

export default function TripoStudio() {
  const [mode, setMode] = useState("text");
  const [prompt, setPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [imageEntries, setImageEntries] = useState([]);
  const [multiviewEntries, setMultiviewEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [taskId, setTaskId] = useState("");
  const [status, setStatus] = useState("");
  const [files, setFiles] = useState([]);
  const [billingStatus, setBillingStatus] = useState(null);
  const [creditEstimate, setCreditEstimate] = useState(1);
  const [billingError, setBillingError] = useState("");
  const modeOptions = [
    { id: "text", label: t("tripo.mode.text") },
    { id: "image", label: t("tripo.mode.image") },
    { id: "multiview", label: t("tripo.mode.multiview") }
  ];

  useEffect(() => {
    async function consumePickerSelection() {
      const params = new URLSearchParams(window.location.search);
      if (params.get("media_picker_done") !== "1") return;
      const target = params.get("target") || "";
      const isImageTarget = target === "tripo_image";
      const isMultiTarget = target === "tripo_multiview";
      if (!isImageTarget && !isMultiTarget) return;
      let handled = false;
      try {
        const raw = window.sessionStorage.getItem("contentos_media_picker_payload");
        if (raw) {
          const payload = JSON.parse(raw);
          const images = Array.isArray(payload.images) ? payload.images : [];
          if (isImageTarget && images[0]) {
            setMode("image");
            setImageEntries([{ url: images[0] }]);
            handled = true;
          }
          if (isMultiTarget && images.length) {
            setMode("multiview");
            setMultiviewEntries(images.slice(0, 4).map((url) => ({ url })));
            handled = true;
          }
          window.sessionStorage.removeItem("contentos_media_picker_payload");
        }
      } catch {
        // ignore
      }
      if (handled) {
        try {
          await fetch("/api/fast/media/selection", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ image_paths: [], target })
          });
        } catch {
          // ignore
        }
      }
      params.delete("media_picker_done");
      params.delete("target");
      window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`.replace(/\?$/, ""));
    }
    consumePickerSelection();
  }, []);

  useEffect(() => {
    let isMounted = true;
    fetchBillingStatus()
      .then((data) => {
        if (!isMounted) return;
        setBillingStatus(data);
      })
      .catch((err) => {
        if (!isMounted) return;
        setBillingError(err.message || t("tripo.error.billing"));
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    fetchCreditEstimate({
      gen_type: "model3d",
      model_id: "tripo"
    })
      .then((data) => {
        if (!isMounted) return;
        setCreditEstimate(Number(data.credits || 1));
      })
      .catch(() => {
        if (!isMounted) return;
        setCreditEstimate(1);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const creditsRemaining = billingStatus?.credits_remaining;
  const creditsNeeded = Number.isFinite(creditEstimate) ? creditEstimate : 1;
  const hasCredits = typeof creditsRemaining === "number" ? creditsRemaining >= creditsNeeded : true;
  const missingCredits =
    typeof creditsRemaining === "number" ? Math.max(creditsNeeded - creditsRemaining, 0) : 0;

  const handleGenerate = async () => {
    if (mode === "text" && !prompt.trim()) return;
    if (mode === "image" && imageEntries.length < 1) return;
    if (mode === "multiview" && multiviewEntries.length < 4) return;
    setLoading(true);
    setErrorMsg("");
    setTaskId("");
    setStatus("");
    setFiles([]);
    try {
      const endpoint =
        mode === "image"
          ? "/api/fast/tripo/image-to-model"
          : mode === "multiview"
            ? "/api/fast/tripo/multiview-to-model"
            : "/api/fast/tripo/text-to-model";
      const payload =
        mode === "image"
          ? { image_url: imageEntries[0]?.url }
          : mode === "multiview"
            ? { image_urls: multiviewEntries.map((entry) => entry.url).filter(Boolean) }
            : {
              prompt: prompt.trim(),
              negative_prompt: negativePrompt.trim()
            };
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        setErrorMsg(data?.error || t("tripo.error.generation"));
      } else {
        setTaskId(data?.task_id || "");
        setStatus(data?.status || "");
        setFiles(Array.isArray(data?.files) ? data.files : []);
      }
    } catch {
      setErrorMsg(t("tripo.error.generation"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell">
      <Sidebar active="tripo" />
      <main className="tripo-page app-main">
        <header className="tripo-header">
          <h1 className="page-title-accent">{t("tripo.title")}</h1>
          <p>{t("tripo.subtitle")}</p>
        </header>
        <section className="tripo-panel video-card">
          <span>{t("nano.mode.label")}</span>
          <div className="video-mode-bar">
            {modeOptions.map((item) => (
              <button
                key={item.id}
                className={mode === item.id ? "video-mode active" : "video-mode"}
                onClick={() => setMode(item.id)}
              >
                <span className="video-mode-dot" />
                {item.label}
              </button>
            ))}
          </div>
          {mode === "text" && (
            <>
              <label htmlFor="tripo-prompt">{t("tripo.label.prompt")}</label>
              <textarea
                id="tripo-prompt"
                rows={5}
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                placeholder={t("tripo.placeholder.prompt")}
              />
              <label htmlFor="tripo-negative">{t("tripo.label.negative")}</label>
              <textarea
                id="tripo-negative"
                rows={3}
                value={negativePrompt}
                onChange={(event) => setNegativePrompt(event.target.value)}
                placeholder={t("tripo.placeholder.negative")}
              />
            </>
          )}
          {mode === "image" && (
            <>
              <MediaSelector
                title="Image source"
                selected={imageEntries}
                onChange={setImageEntries}
                maxCount={1}
                allowLibrary
                pickerTarget="tripo_image"
                pickerReturn="/tripo"
                pickerMulti={false}
              />
            </>
          )}
          {mode === "multiview" && (
            <>
              <MediaSelector
                title="Multiview (4 images)"
                selected={multiviewEntries}
                onChange={setMultiviewEntries}
                maxCount={4}
                minCount={4}
                allowLibrary
                pickerTarget="tripo_multiview"
                pickerReturn="/tripo"
                pickerMulti
              />
            </>
          )}
          <div className="tripo-actions">
            <button
              type="button"
              className="nano-primary"
              onClick={handleGenerate}
              disabled={
                loading ||
                (mode === "text" && !prompt.trim()) ||
                (mode === "image" && imageEntries.length < 1) ||
                (mode === "multiview" && multiviewEntries.length < 4) ||
                !hasCredits
              }
            >
              {loading ? t("nano.generating") : t("tripo.button.generate")}
              <span className="credit-badge">{creditsNeeded}</span>
            </button>
            <span className="tripo-hint">{t("tripo.hint.apiKey")}</span>
          </div>
          {billingError && <div className="tripo-error">{billingError}</div>}
          {!hasCredits && missingCredits > 0 && (
            <button
              type="button"
              className="billing-upgrade-link"
              onClick={() => (window.location.href = "/pricing")}
            >
              {t("tripo.credits.missing", "{missing} / {needed} credits missing, top up!").replace("{missing}", missingCredits).replace("{needed}", creditsNeeded)}
            </button>
          )}
          {errorMsg && <div className="tripo-error">{errorMsg}</div>}
        </section>
        <section className="tripo-results video-card">
          <h2>{t("tripo.results.title")}</h2>
          {taskId && (
            <div className="tripo-status">
              Task: {taskId} {status ? `(${status})` : ""}
            </div>
          )}
          {files.length === 0 && !loading && (
            <div className="tripo-empty">{t("tripo.results.empty")}</div>
          )}
          {files.length > 0 && (
            <div className="tripo-files">
              {files.map((fileUrl) => {
                const label = fileUrl.split("/").pop() || fileUrl;
                if (isRemoteUrl(fileUrl)) {
                  return (
                    <a key={fileUrl} href={fileUrl} className="tripo-file" target="_blank" rel="noreferrer">
                      {label}
                    </a>
                  );
                }
                return (
                  <button
                    key={fileUrl}
                    type="button"
                    className="tripo-file"
                    onClick={() => downloadFile(fileUrl, buildDownloadName(fileUrl))}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
